import { Dispatch, SetStateAction, createContext, useState } from 'react'
import { useQuery } from '~/shared/hooks/query'
import type { OnboardingRetrieveResponse } from '@fireflyhealth/api'
import { onboarding, programsEnrollment } from '@fireflyhealth/api'
import { useQueryClient } from '@tanstack/react-query'

const loadOnboardingState = async (patientId: string | null) => {
  // `state.id` arrives as a string (route param), but the generated client
  // validates `patient_id` against a numeric schema, so coerce it.
  const { data } = await onboarding.retrieve({ patient_id: Number(patientId) })
  return data
}

const loadEnrolledPrograms = async (patientId: string | null) => {
  // `state.id` arrives as a string (route param), but the generated client
  // validates `patient_id` against a numeric schema, so coerce it.
  const { data } = await programsEnrollment.retrieve({
    params: { patient_id: Number(patientId) },
  })
  return data
}

interface PatientDetailState {
  /** `null` until the patient's onboarding record has loaded. */
  onboardingState: OnboardingRetrieveResponse | null
  enrolledPrograms: string[]
  id: string | null
  isLoaded: boolean
  setState: Dispatch<SetStateAction<PatientDetailState>>
  refetch: () => Promise<void>
}

export const INITIAL_STATE: PatientDetailState = {
  onboardingState: null,
  enrolledPrograms: [],
  id: null,
  isLoaded: false,
  setState: () => null,
  refetch: () => Promise.resolve(),
}

export const PatientDetailContext = createContext<PatientDetailState>(INITIAL_STATE)

const useLoadPatientDetails = (patientId: string | null) =>
  useQuery(
    ['loadPatientDetail', patientId],
    () => Promise.all([loadEnrolledPrograms(patientId), loadOnboardingState(patientId)]),
    { enabled: !!patientId },
    { error: `Failed to load details for patient ${patientId}` },
  )

export const PatientDetailContextProvider = ({ children }: React.PropsWithChildren) => {
  const [state, setState] = useState(INITIAL_STATE)
  const { data, isFetched } = useLoadPatientDetails(state.id)
  const client = useQueryClient()
  const refetch = () => client.invalidateQueries({ queryKey: ['loadPatientDetail', state.id] })

  // Each load merges the patient's programs and onboarding state into the context.
  const [appliedData, setAppliedData] = useState<typeof data>()
  if (data && data !== appliedData) {
    setAppliedData(data)
    const [enrolledPrograms, onboardingState] = data
    setState(state => ({ ...state, enrolledPrograms, onboardingState }))
  }

  return (
    <PatientDetailContext.Provider
      value={{
        enrolledPrograms: state.enrolledPrograms,
        onboardingState: state.onboardingState,
        isLoaded: !isFetched,
        id: state.id,
        setState,
        refetch,
      }}
    >
      {children}
    </PatientDetailContext.Provider>
  )
}
